import classnames from 'classnames'
import { abbreviateNumber } from 'js-abbreviation-number'

import type { PortableTextComponentProps } from '~/components/atoms/BlockContent/BlockContent'
import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import LeadParagraph from '~/components/atoms/LeadParagraph/LeadParagraph'
import MagazineImage from '~/components/atoms/MagazineImage/MagazineImage'
import Spacer, { SpacerSizes } from '~/components/atoms/Spacer/Spacer'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import VideoEmbed from '~/components/atoms/VideoEmbed/VideoEmbed'
import type { RecordPricesSlideProps } from '~/components/blocks/RecordPrices/RecordPrices'
import { numberWithCommas } from '~/utils/formatCurrency'
import { toURL } from '~/utils/urlResolver'

import styles from './ArticleNumberedList.module.css'
import numberBlockStyles from '~/components/atoms/NumberBlock/NumberBlock.module.css'

export interface ArticleNumberedListProps {
  slides: RecordPricesSlideProps[]
  intro: PortableTextComponentProps['blocks']
  isPremium?: boolean
  testID?: string
}

export default function ArticleNumberedList({
  slides,
  intro,
  isPremium,
  testID,
}: ArticleNumberedListProps) {
  return (
    <div className={styles.base} data-testid={testID}>
      <LeadParagraph body={intro} _key="lead-para" />

      {slides.map((slide, i) => {
        let slideTitle = slide.price
          ? JSON.stringify(slide.price).length > 6
            ? abbreviateNumber(slide.price, 1)
            : numberWithCommas(slide.price)
          : `${slide.title}`

        if (slide.artworkName) {
          slideTitle += ` for ${slide.artworkName}`
        }

        if (slide.currency) {
          slideTitle = `${slide.currency}${slideTitle}`
        }

        const { originalCurrency } = slide
        const shouldHidePremiumSlides = isPremium && i > 1
        const classNameForHide = shouldHidePremiumSlides ? styles.hidden : ''

        return (
          <div className={classNameForHide}>
            <Fixture
              verticalPadding={true}
              noPaddingBottom={true}
              key={`slide-${i}`}
              className={classNameForHide}
            >
              <Container>
                <div
                  className={classnames(
                    numberBlockStyles.base,
                    numberBlockStyles.magazine,
                    isPremium && i === 1 && styles.fadeOutModule
                  )}
                >
                  <div
                    className={classnames(
                      numberBlockStyles.title,
                      styles.title
                    )}
                  >
                    {!slide.price && (
                      <Text
                        className={numberBlockStyles.number}
                        size={TextSizes.magPrimaryTitleLrg}
                      >
                        {i + 1}.
                      </Text>
                    )}
                    <Text size={TextSizes.magPrimaryTitleSml} Tag="h2">
                      {slideTitle}
                    </Text>
                    {originalCurrency && (
                      <Text
                        Tag="p"
                        size={TextSizes.secondaryTitleMedReg}
                        className={styles.originalCurrency}
                      >
                        ({originalCurrency})
                      </Text>
                    )}
                  </div>
                  {slide.videoSrc && slide.videoType && (
                    <VideoEmbed
                      videoSrc={slide.videoSrc}
                      videoType={slide.videoType}
                      className={styles.slideVideoWrapper}
                    />
                  )}
                  {slide.image && !slide.videoSrc && (
                    <MagazineImage
                      image={slide.image}
                      artwork={slide.artwork}
                      link={slide.artwork && (toURL(slide.artwork.url) as any)}
                    />
                  )}

                  <Spacer size={SpacerSizes.md} />

                  <div
                    className={classnames(numberBlockStyles.bodyText, 'prose')}
                  >
                    <BlockContent value={slide.text} size={TextSizes.bodyLrg} />
                  </div>
                </div>
              </Container>
            </Fixture>
          </div>
        )
      })}
    </div>
  )
}
